import moment from 'moment';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';

const SectionContainer = styled.div`
  margin-top: 24px;
`;

const SectionHeader = styled.div`
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 4px 0;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0;
`;

// Ticket Picker (Dropdown)
const PickerContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const PickerInput = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px 10px 12px;
  background: transparent;
  border: 1px dashed
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.4)'
        : 'rgba(250, 70, 22, 0.5)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.08)'
        : 'rgba(250, 70, 22, 0.05)'};
    border-color: #fa4616;
  }

  &:focus-within {
    background: ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.08)'
        : 'rgba(250, 70, 22, 0.05)'};
    border-color: #fa4616;
    border-style: solid;
  }
`;

const PickerIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fa4616;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const PickerText = styled.input`
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  min-width: 200px;

  &::placeholder {
    color: #fa4616;
    opacity: 0.8;
  }
`;

const PickerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 400px;
  max-width: 100%;
  background: ${({ theme }) => (theme.name === 'dark' ? '#252542' : '#ffffff')};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: 10px;
  box-shadow: ${({ theme }) =>
    theme.name === 'dark'
      ? '0 12px 32px rgba(0, 0, 0, 0.5)'
      : '0 12px 32px rgba(0, 0, 0, 0.15)'};
  max-height: 320px;
  overflow-y: auto;
  z-index: 100;
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)'};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.card.border};
  }
`;

const DropdownTicketInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DropdownTicketName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownTicketMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  margin-top: 2px;
`;

const DropdownTicketPrice = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DropdownEmpty = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textGray.muted};
  font-size: 14px;
`;

// Selected Tickets List
const SelectedTicketsContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: 8px;
  overflow: hidden;
`;

const SelectedTicketRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.card.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.card.border};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const TicketInfo = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 600px) {
    flex-basis: calc(100% - 44px);
  }
`;

const TicketName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TicketMeta = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin-top: 2px;
`;

const TicketPrice = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 70px;
  text-align: right;

  @media (max-width: 600px) {
    display: none;
  }
`;

const QtyControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 600px) {
    margin-left: auto;
  }
`;

const QtyButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.02)'};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)'};
    border-color: ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.2)'};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const QtyValue = styled.span`
  min-width: 32px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RemoveButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textGray.muted};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
`;

const EmptySelected = styled.div`
  padding: 32px 24px;
  text-align: center;
  background: ${({ theme }) => theme.colors.card.background};
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;
`;

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
`;

const EmptyDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.muted};
`;

// Icons
const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const TicketIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M13 5v2" />
    <path d="M13 17v2" />
    <path d="M13 11v2" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

// Format price as currency
const formatPrice = (price) => {
  const num = Number(price) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

// Format date for display
const formatDate = (startDate, endDate) => {
  if (!startDate) return '';
  const start = moment(startDate, 'MM-DD-YYYY');
  if (!start.isValid()) return startDate;

  const formattedStart = start.format('ddd MMM D');

  if (endDate && startDate !== endDate) {
    const end = moment(endDate, 'MM-DD-YYYY');
    if (end.isValid() && start.format('D') !== end.format('D')) {
      return `${formattedStart} - ${end.format('D')}`;
    }
  }

  return formattedStart;
};

const BundleTicketsSection = ({
  availableTickets = [],
  selectedTickets = [],
  onChange,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get tickets that haven't been selected yet
  const availableToAdd = useMemo(() => {
    const selectedNames = new Set(selectedTickets.map((t) => t.name));
    return availableTickets.filter((t) => !selectedNames.has(t.name));
  }, [availableTickets, selectedTickets]);

  // Filter by search query
  const filteredTickets = useMemo(() => {
    if (!searchQuery.trim()) return availableToAdd;
    const query = searchQuery.toLowerCase();
    return availableToAdd.filter((t) => t.name.toLowerCase().includes(query));
  }, [availableToAdd, searchQuery]);

  // Add a ticket to the bundle
  const handleAddTicket = (ticket) => {
    const newSelectedTickets = [
      ...selectedTickets,
      { id: ticket.id, name: ticket.name, quantity: 1 },
    ];
    onChange(newSelectedTickets);
    setSearchQuery('');
    setIsDropdownOpen(false);
  };

  // Update quantity for a ticket
  const handleQuantityChange = (ticketName, delta) => {
    const newSelectedTickets = selectedTickets.map((t) => {
      if (t.name === ticketName) {
        const newQty = Math.max(1, (t.quantity || 1) + delta);
        return { ...t, quantity: newQty };
      }
      return t;
    });
    onChange(newSelectedTickets);
  };

  // Remove a ticket from the bundle
  const handleRemoveTicket = (ticketName) => {
    const newSelectedTickets = selectedTickets.filter(
      (t) => t.name !== ticketName
    );
    onChange(newSelectedTickets);
  };

  // Get full ticket info from available tickets
  const getTicketInfo = (ticketName) => {
    return availableTickets.find((t) => t.name === ticketName);
  };

  const handleInputClick = () => {
    setIsDropdownOpen(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>Tickets in This Bundle</SectionTitle>
        <SectionDescription>
          Add tickets to include in this bundle and set the quantity for each.
        </SectionDescription>
      </SectionHeader>

      {/* Ticket Picker */}
      <PickerContainer ref={containerRef}>
        <PickerInput onClick={handleInputClick}>
          <PickerIcon>
            <PlusIcon />
          </PickerIcon>
          <PickerText
            ref={inputRef}
            type="text"
            placeholder="Add ticket to bundle..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => setIsDropdownOpen(true)}
          />
        </PickerInput>

        {isDropdownOpen && (
          <PickerDropdown>
            {filteredTickets.length === 0 ? (
              <DropdownEmpty>
                {availableToAdd.length === 0
                  ? 'All available tickets have been added'
                  : 'No tickets match your search'}
              </DropdownEmpty>
            ) : (
              filteredTickets.map((ticket) => (
                <DropdownItem
                  key={ticket.id || ticket.name}
                  onClick={() => handleAddTicket(ticket)}
                >
                  <DropdownTicketInfo>
                    <DropdownTicketName>{ticket.name}</DropdownTicketName>
                    <DropdownTicketMeta>
                      {formatDate(ticket.start_date, ticket.end_date)}
                    </DropdownTicketMeta>
                  </DropdownTicketInfo>
                  <DropdownTicketPrice>
                    {formatPrice(ticket.price)}
                  </DropdownTicketPrice>
                </DropdownItem>
              ))
            )}
          </PickerDropdown>
        )}
      </PickerContainer>

      {/* Selected Tickets List */}
      <SelectedTicketsContainer>
        {selectedTickets.length === 0 ? (
          <EmptySelected>
            <EmptyIcon>
              <TicketIcon />
            </EmptyIcon>
            <EmptyTitle>No tickets selected</EmptyTitle>
            <EmptyDescription>
              Use the search above to add tickets to this bundle
            </EmptyDescription>
          </EmptySelected>
        ) : (
          selectedTickets.map((selected) => {
            const ticketInfo = getTicketInfo(selected.name);
            return (
              <SelectedTicketRow key={selected.name}>
                <TicketInfo>
                  <TicketName>{selected.name}</TicketName>
                  <TicketMeta>
                    {ticketInfo
                      ? formatDate(ticketInfo.start_date, ticketInfo.end_date)
                      : ''}
                  </TicketMeta>
                </TicketInfo>
                <TicketPrice>
                  {ticketInfo ? formatPrice(ticketInfo.price) : ''}
                </TicketPrice>
                <QtyControl>
                  <QtyButton
                    type="button"
                    onClick={() => handleQuantityChange(selected.name, -1)}
                    disabled={selected.quantity <= 1}
                  >
                    -
                  </QtyButton>
                  <QtyValue>{selected.quantity || 1}</QtyValue>
                  <QtyButton
                    type="button"
                    onClick={() => handleQuantityChange(selected.name, 1)}
                  >
                    +
                  </QtyButton>
                </QtyControl>
                <RemoveButton
                  type="button"
                  onClick={() => handleRemoveTicket(selected.name)}
                  title="Remove from bundle"
                >
                  <TrashIcon />
                </RemoveButton>
              </SelectedTicketRow>
            );
          })
        )}
      </SelectedTicketsContainer>
    </SectionContainer>
  );
};

export default BundleTicketsSection;
